/**
 * Setup orchestrator — the single entry point that decides which setup
 * surface (if any) to show on each app boot, and handles the `#?setup=1`
 * and `#?tour=1` deep-link re-entries.
 *
 * Replaces the previous arrangement where firstRunTour fired from main.js
 * and onboarding.js fired from home.js — those raced and stacked on top
 * of each other. The orchestrator enforces "only one setup surface
 * visible at a time" by gating each call on the others.
 *
 * Decision matrix (boot, in order):
 *   1. No user / Playwright             → do nothing
 *   2. user.email_verified === false    → leave the verify-email banner alone (renderVerifyBanner owns that surface)
 *   3. user.onboarding_completed false  → start setup wizard
 *   4. user.tour_completed false        → drop a one-time tour_reminder bell
 *      notification (B1 F-UX-2: the tour no longer auto-starts — it stacked
 *      on the checklist/hero and blocked first clicks). Re-entry paths:
 *      the bell link (#?tour=1), the per-page "Tour this page" chip, and
 *      the command palette's restartTour().
 *   5. else                             → idle (deep-links can still fire below)
 */

import { startSetupWizard, isSetupWizardOpen } from './setupWizard.js';
import { startCoachmarkTour, resumeCoachmarkTour, isCoachmarkActive, abortCoachmarkTour } from './coachmarkTour.js';

let listenersWired = false;
let decisionFired = false;

export function bootSetupOrchestrator() {
    // Wire the hashchange listener exactly once (idempotent).
    if (!listenersWired) {
        listenersWired = true;
        window.addEventListener('hashchange', () => {
            if (consumeHashFlag('setup')) {
                if (window.__auth__ && !isSetupWizardOpen()) startSetupWizard({ resume: true });
            } else if (consumeHashFlag('tour')) {
                // resumeCoachmarkTour reads BOTH the persisted playlist name and
                // step index. startCoachmarkTour(undefined, {resume}) defaulted
                // the playlist to 'heist' while resuming a step index persisted
                // by whatever tour ran last — landing mid-heist (or past its
                // end) after e.g. an interrupted analytics tour.
                if (window.__auth__ && !isCoachmarkActive()) resumeCoachmarkTour();
            }
        });
        // Re-evaluate the decision when the global auth state changes
        // (login, logout, impersonate, end-impersonate). Without this, the
        // first-run wizard never fires for users whose /me payload arrives
        // after the initial boot pass.
        // Also reset decisionFired on logout so the NEXT login can fire
        // the wizard for that user (otherwise a "decisionFired" set during
        // the previous session sticks across the auth swap and no-ops the
        // wizard for the new user).
        window.addEventListener('heist:auth-changed', () => {
            if (!window.__auth__) {
                decisionFired = false;
                return;
            }
            retrySetupDecision();
        });
    }

    // Honour explicit deep-links first (they bypass the boot decision)
    if (consumeHashFlag('setup')) {
        if (window.__auth__ && !isSetupWizardOpen()) startSetupWizard({ resume: true });
        decisionFired = true;
        return;
    }
    if (consumeHashFlag('tour')) {
        if (window.__auth__ && !isCoachmarkActive()) resumeCoachmarkTour();
        decisionFired = true;
        return;
    }
    if (bootDecision()) decisionFired = true;
}

/**
 * Re-trigger the boot decision. Called on heist:auth-changed so that
 * impersonation (which swaps `window.__auth__` mid-session) gets the
 * wizard/tour for the impersonated user. No-op if a decision already
 * surfaced something this session.
 */
export function retrySetupDecision() {
    if (decisionFired) return;
    if (bootDecision()) decisionFired = true;
}

function bootDecision() {
    const u = window.__auth__;
    if (!u || !u.id) {
        console.log('[ORCHESTRATOR] no __auth__ yet — will retry on auth-changed');
        return false;
    }
    // Phase 41 — a social-connect OAuth round-trip from the wizard lands the
    // user back on #settings. Reopen the wizard at the channels step so they
    // don't get stranded there. This fires even when onboarding_completed is
    // true (the user may have opened the wizard manually to add a channel).
    // One-shot: the flag is cleared before reopening.
    try {
        if (localStorage.getItem('heist.setup.resumeAfterOauth') === '1') {
            localStorage.removeItem('heist.setup.resumeAfterOauth');
            console.log('[ORCHESTRATOR] resuming wizard after social-connect OAuth for', u.email);
            if (!isSetupWizardOpen()) startSetupWizard({ resume: true });
            return true;
        }
    } catch (_) { /* localStorage unavailable — fall through to normal decision */ }
    // One-line diagnostic so "wizard didn't fire" reports are debuggable
    // from the console without redeploying.
    console.log('[ORCHESTRATOR] boot user=' + u.email + ' onboarding_completed=' + !!u.onboarding_completed + ' tour_completed=' + !!u.tour_completed);
    if (typeof navigator !== 'undefined' && navigator.webdriver) return true; // Playwright
    // Honour the legacy localStorage tour flag once — sync to server then forget.
    try {
        if (localStorage.getItem('heist.tour.completedV1') === '1' && !u.tour_completed) {
            import('../api/client.js').then(({ client }) => client.completeTour({}).catch(() => {}));
            u.tour_completed = true;
            localStorage.removeItem('heist.tour.completedV1');
        }
    } catch (_) {}

    // An explicit "Skip setup" is persisted server-side (setup_skipped_at)
    // so the wizard doesn't re-fire on every boot for a user who opted out.
    // The bell notification (setup_incomplete → #?setup=1) and the command
    // palette remain the re-entry paths; finishing the wizard clears the flag.
    if (!u.onboarding_completed && !u.setup_skipped) {
        console.log('[ORCHESTRATOR] firing setup wizard for', u.email);
        startSetupWizard({ resume: true });
        return true;
    }

    if (!u.tour_completed) {
        // Quiet re-entry instead of auto-start: mark the tour skipped
        // server-side, which idempotently drops the `tour_reminder` bell
        // notification (same handler the tour's own Skip button uses).
        // One-shot — tour_completed flips so this never re-fires.
        console.log('[ORCHESTRATOR] tour pending — dropping tour_reminder notification for', u.email);
        import('../api/client.js').then(({ client }) => client.completeTour({ skipped: true }).catch(() => {}));
        u.tour_completed = true;
        return true;
    }
    console.log('[ORCHESTRATOR] user fully set up — no surface to show');
    return true;
}

/**
 * Looks for `?<flag>=1` inside the current hash (e.g. `#home?tour=1`). If
 * present, removes it from the URL so the action is one-shot, and returns
 * true. Otherwise returns false.
 */
function consumeHashFlag(flag) {
    const h = window.location.hash || '';
    const re = new RegExp('([?&])' + flag + '=1\\b');
    if (!re.test(h)) return false;
    const cleaned = h.replace(re, '$1').replace(/[?&]$/, '');
    history.replaceState(null, '', window.location.pathname + window.location.search + cleaned);
    return true;
}

/**
 * Public entry point for re-firing a tour playlist. Used by:
 *  - The command-palette "Restart site tour" entry.
 *  - The per-page "?" button rendered by pageHeader.js (Phase 2).
 *
 * Aborts any in-flight tour first so the caller doesn't get a silent no-op
 * when activeOverlay still holds a stale reference (e.g. user clicked the
 * "?" button while another tour was showing, or after a previous tour's
 * DOM was removed externally).
 */
export function restartTour(name = 'heist') {
    if (isCoachmarkActive()) abortCoachmarkTour();
    startCoachmarkTour(name);
}

/**
 * Public command-palette entry point: explicitly resume the wizard.
 */
export function resumeWizard() {
    startSetupWizard({ resume: true });
}
